import { useQuery } from "@tanstack/react-query";

import type { BillingEstimate } from "features/subscriptions/types";
import type { getCouponUpdateState } from "features/subscriptions/utils";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import type { Pricing } from "common/types";

type CouponUpdateState = ReturnType<typeof getCouponUpdateState>;

async function workspaceEstimateUpdateRequest(
  workspaceId: string,
  subscriptionId: string,
  planId: string,
  addonId: string,
  quantity: number,
  prorate: boolean,
  couponUpdateState?: CouponUpdateState,
): Promise<BillingEstimate> {
  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/estimates/update`,
    {
      items: [
        { id: planId },
        ...(quantity > 0 ? [{ id: addonId, quantity }] : []),
      ],
      prorate,
      ...(couponUpdateState && {
        coupon: couponUpdateState.coupon,
        remove_coupon: couponUpdateState.removeCoupon,
      }),
    },
  );
  return estimate;
}

async function customerEstimateUpdateRequest(
  planId: string,
  quantity: number,
  prorate: boolean,
  couponUpdateState?: CouponUpdateState,
): Promise<BillingEstimate> {
  const { estimate } = await post("subscriptions/estimates/update", {
    item_price_id: planId,
    seat_count: quantity,
    prorate,
    ...(couponUpdateState && {
      coupon: couponUpdateState.coupon,
      remove_coupon: couponUpdateState.removeCoupon,
    }),
  });
  return estimate;
}

// if subscriptionId is true, uses the workspace endpoint
export default function useEstimateUpdate({
  pricing,
  quantity,
  isProrated,
  couponUpdateState,
  subscriptionId,
  isEnabled = true,
}: {
  pricing: { addon: Pricing; plan: Pricing } | undefined;
  quantity: number;
  isProrated: boolean;
  couponUpdateState?: CouponUpdateState;
  subscriptionId?: string;
  isEnabled?: boolean;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const isCustomerSubscription = !subscriptionId;

  const {
    data: estimateUpdate,
    isFetching: isLoadingEstimateUpdate,
    error: estimateUpdateError,
  } = useQuery({
    queryKey: isCustomerSubscription
      ? CUSTOMER_QUERY_KEYS.subscriptionKeys.updateEstimate(
          pricing?.plan.id,
          quantity,
          isProrated,
          couponUpdateState,
        )
      : getQueryKeys(workspaceId).subscriptionKeys.updateEstimate(
          pricing?.plan.id,
          quantity,
          isProrated,
          couponUpdateState,
        ),
    queryFn: isCustomerSubscription
      ? () =>
          customerEstimateUpdateRequest(
            pricing?.plan.id,
            quantity,
            isProrated,
            couponUpdateState,
          )
      : () =>
          workspaceEstimateUpdateRequest(
            workspaceId,
            subscriptionId,
            pricing?.plan.id,
            pricing?.addon.id,
            quantity,
            isProrated,
            couponUpdateState,
          ),
    enabled: !!pricing && isEnabled,
    gcTime: 0,
  });

  return { estimateUpdate, isLoadingEstimateUpdate, estimateUpdateError };
}
